import React, { Suspense, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Dropdown,
  Empty,
  Modal,
  Skeleton,
  Space,
  Tag,
  Tooltip,
  Typography
} from 'antd';
import Icon, {
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  MoreOutlined
} from '@ant-design/icons';
import Fuse from 'fuse.js';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { IntegrationItemDto } from '@api/Api';
import {
  AvailableIntegration,
  availableIntegrations
} from '@pages/AdminSettings/components/IntegrationTab/constants';
import { apiClient } from '@api/client';
import UpgradePlanTooltip from '@components/Tooltip/UpgradePlanTooltip';
import { DiamondUpgradeIcon } from '@icons';
import { figmaAlert, figmaSettingsQuery } from '@helpers/figmaPosting';
import { ConfirmModal } from '@components/Modals';
import './InstalledIntegrations.less';

const { Title, Paragraph, Text } = Typography;

interface IntegrationCardProps {
  integration: IntegrationItemDto & {
    typeName: string;
    description: string;
    logo?: AvailableIntegration['logo'];
    tags: AvailableIntegration['tags'];
    details?: AvailableIntegration['installedDetails'];
  };
  onDetailsClick: () => void;
  onErrorClick: () => void;
  refetchInstalledIntegrations: () => Promise<void>;
}

function IntegrationCard({
  integration,
  onDetailsClick,
  onErrorClick,
  refetchInstalledIntegrations
}: IntegrationCardProps) {
  const [name, setName] = useState(integration.name || '');
  const [editingName, setEditingName] = useState(false);
  const renameMutation = useMutation({
    mutationFn: async (name: string) => {
      return await apiClient.integration.integrationControllerRenameIntegration(
        { id: integration.id, name }
      );
    }
  });
  useEffect(() => {
    setName(integration.name || '');
  }, [integration.name]);

  const uninstallMutation = useMutation({
    mutationFn: async () => {
      return await apiClient.integration.integrationControllerUninstallIntegration(
        { id: integration.id }
      );
    },
    onSettled: async () => {
      await refetchInstalledIntegrations();
    }
  });
  const upgradeRequired =
    integration.status === 'error' &&
    integration.error?.startsWith('integration_not_allowed_');
  // Figma's own problems (posting account, comments not posted) open its
  // details, where an admin can fix them.
  const figmaSettings = useQuery({
    ...figmaSettingsQuery(integration.id),
    enabled: integration.type === 'figma' && !upgradeRequired
  });
  const alert = figmaSettings.data ? figmaAlert(figmaSettings.data) : null;

  const [confirmingUninstall, setConfirmingUninstall] = useState(false);
  const confirmUninstall = () => setConfirmingUninstall(true);
  const menu = {
    items: [
      { key: 'rename', label: name ? 'Rename' : 'Add a name' },
      { type: 'divider' as const },
      { key: 'uninstall', label: 'Uninstall', danger: true }
    ],
    onClick: ({
      key,
      domEvent
    }: {
      key: string;
      domEvent: React.SyntheticEvent;
    }) => {
      domEvent.stopPropagation();
      if (key === 'rename') setEditingName(true);
      if (key === 'uninstall') confirmUninstall();
    }
  };
  const saveName = (value: string) => {
    setEditingName(false);
    const next = value.trim();
    if (next === name) return;
    setName(next);
    renameMutation.mutate(next);
  };

  const card = (
    <Card
      className="installed_integration"
      style={{
        height: '100%',
        cursor: upgradeRequired ? 'not-allowed' : undefined
      }}
      hoverable={!!integration.details && !upgradeRequired}
      onClick={upgradeRequired || editingName ? undefined : onDetailsClick}
    >
      <div className="installed_integration-head">
        {integration.logo ? <integration.logo width={56} height={56} /> : null}
        <div className="installed_integration-names">
          <Text strong className="installed_integration-type">
            {integration.typeName}
          </Text>
          <div onClick={(e) => e.stopPropagation()}>
            {name || editingName ? (
              <Text
                type="secondary"
                editable={{
                  editing: editingName,
                  maxLength: 255,
                  text: name,
                  tooltip: false,
                  triggerType: ['text'],
                  onStart: () => setEditingName(true),
                  onChange: saveName,
                  onCancel: () => setEditingName(false)
                }}
              >
                {name}
              </Text>
            ) : (
              <Text
                type="secondary"
                className="installed_integration-add-name"
                onClick={() => setEditingName(true)}
              >
                Add a name
              </Text>
            )}
          </div>
        </div>
        {!upgradeRequired && (
          <div
            className="installed_integration-actions"
            onClick={(e) => e.stopPropagation()}
          >
            {alert ? (
              <Tooltip
                title={`${alert.title}. ${alert.text}`}
                overlayClassName="toggle_favorite_overlay"
              >
                <Tag
                  icon={
                    alert.level === 'error' ? (
                      <CloseCircleOutlined />
                    ) : (
                      <ExclamationCircleOutlined />
                    )
                  }
                  color={alert.level}
                  role="button"
                  style={{ cursor: 'pointer', marginRight: 0 }}
                  onClick={onDetailsClick}
                >
                  {alert.level === 'error' ? 'Error' : 'Attention'}
                </Tag>
              </Tooltip>
            ) : null}
            {integration.status === 'error' ? (
              <Tag
                icon={<CloseCircleOutlined />}
                color="error"
                role="button"
                style={{ cursor: 'pointer', marginRight: 0 }}
                onClick={onErrorClick}
              >
                Error
              </Tag>
            ) : null}
            <Dropdown menu={menu} trigger={['click']} placement="bottomRight">
              <Button
                type="text"
                size="small"
                aria-label="More actions"
                loading={uninstallMutation.isLoading}
                icon={<MoreOutlined />}
              />
            </Dropdown>
          </div>
        )}
      </div>
      <Paragraph style={{ marginTop: 12 }}>
        {integration.tags.map((x) => (
          <Tag key={x} style={{ marginTop: 8 }}>
            {x}
          </Tag>
        ))}
      </Paragraph>
      <Paragraph style={{ marginBottom: 0 }}>
        {integration.description}
      </Paragraph>
      <div onClick={(e) => e.stopPropagation()}>
        <ConfirmModal
          visible={confirmingUninstall}
          title={`Uninstall ${name || integration.typeName}?`}
          description="It stops working for everyone in the organization. You can install it again later."
          okText="Uninstall"
          danger
          onCancel={() => setConfirmingUninstall(false)}
          onConfirm={() => {
            setConfirmingUninstall(false);
            uninstallMutation.mutate();
          }}
        />
      </div>
    </Card>
  );
  if (upgradeRequired) {
    const upgradeText =
      integration.error === 'integration_not_allowed_advanced'
        ? ' to an Enterprise plan to use this integration.'
        : ' to a Team plan to use this integration.';
    return (
      <Badge.Ribbon
        text={
          <UpgradePlanTooltip text={upgradeText} isCanVisible>
            <Space align="center" size={4}>
              <Icon className="upgrade-icon" component={DiamondUpgradeIcon} />
              Upgrade Required
            </Space>
          </UpgradePlanTooltip>
        }
        color="#353839"
      >
        {card}
      </Badge.Ribbon>
    );
  }
  return card;
}

interface InstalledIntegrationsProps {
  searchQuery: string;
  list: IntegrationItemDto[];
  refetchInstalledIntegrations: () => Promise<any>;
}

export default function InstalledIntegrations({
  searchQuery,
  list,
  refetchInstalledIntegrations
}: InstalledIntegrationsProps) {
  const allIntegrations = useMemo(() => {
    return list.map((x) => {
      const available = availableIntegrations[x.type];
      return {
        ...x,
        typeName: available?.name || '',
        description: available?.description || '',
        logo: available?.logo,
        tags: available?.tags || [],
        details: available?.installedDetails
      };
    });
  }, [list]);

  const fuseSearch = useMemo(
    () =>
      new Fuse(allIntegrations, {
        keys: ['name', 'typeName', 'description', 'tags'],
        threshold: 0.3
      }),
    [allIntegrations]
  );

  const integrations = useMemo(
    () =>
      searchQuery
        ? fuseSearch.search(searchQuery).map((result) => result.item)
        : allIntegrations,
    [fuseSearch, allIntegrations, searchQuery]
  );

  const [errorItem, setErrorItem] = useState<(typeof integrations)[number]>();
  const location = useLocation();
  const navigate = useNavigate();
  const openIntegrationId = (
    location.state as { openIntegrationId?: string } | null
  )?.openIntegrationId;
  const [detailsItem, setDetailsItem] = useState<
    (typeof integrations)[number] & { key: string }
  >();

  // Opened from Available (one per organization): show its details once.
  useEffect(() => {
    if (!openIntegrationId) return;
    const item = allIntegrations.find((x) => x.id === openIntegrationId);
    if (item?.details) setDetailsItem({ ...item, key: crypto.randomUUID() });
    navigate('.', { replace: true, state: null });
  }, [openIntegrationId, allIntegrations, navigate]);

  if (!integrations.length) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="No active integrations found"
        style={{
          gridColumnStart: 1,
          gridColumnEnd: -1
        }}
      />
    );
  }

  return (
    <>
      {integrations.map((x) => (
        <IntegrationCard
          key={x.id}
          integration={x}
          onErrorClick={() => setErrorItem(x)}
          onDetailsClick={() =>
            setDetailsItem({ ...x, key: crypto.randomUUID() })
          }
          refetchInstalledIntegrations={refetchInstalledIntegrations}
        />
      ))}
      <Modal
        open={!!detailsItem?.details}
        onCancel={() => setDetailsItem(undefined)}
        footer={null}
        width={800}
        centered
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            maxHeight: 'min(85vh, 1024px)',
            minHeight: 0
          }}
        >
          <Suspense
            fallback={
              <>
                <Skeleton />
                <Skeleton />
                <Skeleton />
                <Skeleton />
              </>
            }
          >
            {detailsItem?.details ? (
              <detailsItem.details
                key={detailsItem.key}
                uniqueKey={detailsItem.key}
                integration={detailsItem}
                onClose={() => setDetailsItem(undefined)}
              />
            ) : null}
          </Suspense>
        </div>
      </Modal>
      <Modal
        open={!!errorItem}
        footer={null}
        onCancel={() => setErrorItem(undefined)}
        width={560}
        destroyOnClose
      >
        <Title level={3} style={{ marginBottom: 8 }}>
          {errorItem?.name}
        </Title>

        <Alert
          type="error"
          showIcon
          message="The integration reported an error"
          description={
            <Paragraph style={{ marginTop: 8 }}>
              <Text code copyable>
                {errorItem?.error ??
                  'No additional error message was provided.'}
              </Text>
            </Paragraph>
          }
        />
      </Modal>
    </>
  );
}
